import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Search, Loader2 } from "lucide-react";
import { getBounced } from "../../services/dataService";
import { DataTable } from "../DataTable";
import { PageHeader, StatCard } from "../PageHeader";
import { StatusBadge } from "../StatusBadge";
import { Input } from "../ui/input";
import { formatCurrency, formatDate } from "../../lib/format";

export function BouncedOrders() {
  const [q, setQ] = useState("");
  const { data = [], isLoading } = useQuery({ queryKey: ["bounced"], queryFn: getBounced });

  const filtered = data.filter(
    (r) =>
      r.client_name.toLowerCase().includes(q.toLowerCase()) ||
      r.reference.toLowerCase().includes(q.toLowerCase()) ||
      r.bounce_reason.toLowerCase().includes(q.toLowerCase())
  );

  const columns = [
    { key: "bounce_date", header: "Bounce Date", sortable: true, accessor: (r) => new Date(r.bounce_date).getTime(), render: (r) => formatDate(r.bounce_date) },
    { key: "client_name", header: "Client Name", sortable: true },
    { key: "reference", header: "Reference", mono: true },
    { key: "amount", header: "Amount", sortable: true, align: "right", mono: true, render: (r) => formatCurrency(r.amount) },
    { key: "bounce_reason", header: "Bounce Reason", render: (r) => <span className="text-slate-700 dark:text-slate-200">{r.bounce_reason}</span> },
    { key: "bounce_code", header: "Code", mono: true, render: (r) => <span className="font-semibold text-rose-600">{r.bounce_code}</span> },
    { key: "retry_status", header: "Retry", render: (r) => <StatusBadge status={r.retry_status} /> },
  ];

  return (
    <div>
      <PageHeader title="Bounced Debit Orders" icon={AlertTriangle} description="All debit orders that bounced or failed since inception, with bounce reasons and retry status." />

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <StatCard label="Total Bounced" value={data.length} accent="rose" />
        <StatCard label="Bounced Value" value={formatCurrency(data.reduce((s, r) => s + r.amount, 0))} accent="rose" />
        <StatCard label="Awaiting Retry" value={data.filter((r) => r.retry_status === "Not Retried").length} accent="amber" />
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client, reference or reason…" className="pl-9" data-testid="bounced-search-input" />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
      ) : (
        <DataTable columns={columns} data={filtered} initialSort={{ key: "bounce_date", dir: "desc" }} emptyText="No bounced debit orders. 🎉" testId="bounced-table" />
      )}
    </div>
  );
}
